<!--
  The stack menu's panel (opened by StackChip): the stack's pull requests,
  base first, as two-line rows on a rail.

  Line 1 is the pull request's number and title; the title takes what
  width is left and is never squeezed out (it is cut with an ellipsis only
  past the panel's widest, and read in full on hover). Line 2 is its
  state, checks, review decision and the viewer's recorded progress, each
  in a column shared by every row, so they line up down the list. The
  rail has one node per row, centred on line 1, joined by one line; a
  node's fill says the pull request's review state, and nothing is drawn
  over it. This pull request's row is marked in the row itself: a bar,
  a tint, and a tag at the end of line 2.

  The panel is as wide as its content between 420px and 640px, and as
  wide as the window allows on a phone. Every style here is the panel's
  own: it borrows no class from the dashboard's rail.
-->
<script lang="ts">
  import { progressWords } from '../logic/prSummary';
  import type { StackMenu, StackMenuPr } from '../logic/prStack';
  import { stateText, type CheckState } from '../logic/stackState';
  import { relTime } from '../logic/time';
  import { prPath, type PrTab } from '../router';
  import type { Status } from '../runtime/remote';
  import { prSummaries } from '../state/prSummaries.svelte';

  interface Props {
    menu: StackMenu;
    /** The view the page is on: a member opens on the same one. */
    tab: PrTab;
    /** What is said under the list of how current the stack is; nothing when it is. */
    status: Status;
    /** Checking an older stack with GitHub failed: the foot offers to check again. */
    recheck: boolean;
    onpick: (e: MouseEvent, r: StackMenuPr) => void;
    onretry: () => void;
  }
  let { menu, tab, status, recheck, onpick, onretry }: Props = $props();

  const STATE_TEXT = { open: 'Open', draft: 'Draft', merged: 'Merged', closed: 'Closed' } as const;
  const CI_TEXT: Record<CheckState, string> = { passing: 'CI passing', failing: 'CI failing', pending: 'CI running', none: '' };
  const REVIEW_TEXT = { approved: 'approved', changes: 'changes requested' } as const;

  const href = (r: StackMenuPr) => prPath(r.owner, r.repo, r.number, tab);
  /** The viewer's recorded progress on a member at its head, in the words the dashboard's rows use. */
  function progress(r: StackMenuPr) {
    const s = prSummaries.get(`${r.owner}/${r.repo}`, r.number, r.head);
    if (!s) return null;
    const w = progressWords(s);
    return { ...w, percent: Math.round(w.share * 100), title: `${w.full}, on this device as of ${relTime(new Date(s.at).toISOString())}` };
  }
  /** The stack forks somewhere: the rail keeps a second lane. */
  const forked = $derived(menu.rows.some((r) => r.lane === 1));
</script>

<div class="sm" class:forked>
  <div class="sm-head">
    <span class="sm-name" aria-hidden="true">Stack of {menu.count}</span>
    <span class="sm-hint"
      ><span aria-hidden="true">base first</span>{#if menu.inferred}<span
          class="sm-inferred"
          title="GitHub names no stack here (or not all of it): Changeling found it from the pull requests' base and head branches.{menu.cut ? ' Only the repository’s most recently updated open pull requests were read, so members may be missing.' : ''}"
          >from branches{menu.cut ? ', may be incomplete' : ''}</span
        >{/if}</span
    >
  </div>
  <div class="sm-list">
    {#each menu.rows as r, i (r.kind + r.index)}
      {#if r.kind === 'pr'}
        {@const p = progress(r)}
        <a
          role="menuitem"
          tabindex="-1"
          class="sm-row"
          class:first={i === 0}
          class:last={i === menu.rows.length - 1}
          class:lane1={r.lane === 1}
          class:current={r.current}
          aria-current={r.current ? 'page' : undefined}
          href={href(r)}
          onclick={(e) => onpick(e, r)}
        >
          <span class="sm-rail" aria-hidden="true"><span class="sm-node {r.member} {r.state}" title="{r.index} of {menu.count}: {stateText(r.member)}">{r.index}</span></span>
          <span class="sr-only">{r.index} of {menu.count}:</span>
          <span class="sm-line">
            <span class="sm-num">#{r.number}</span>
            <span class="sm-title" title={r.title}>{r.title}</span>
          </span>
          <span class="sm-cell sm-state {r.state}">{STATE_TEXT[r.state]}</span>
          <span class="sm-cell sm-ci {r.checks}">{CI_TEXT[r.checks]}</span>
          <span class="sm-cell sm-review {r.review ?? ''}">{r.review ? REVIEW_TEXT[r.review] : ''}</span>
          <span class="sm-cell sm-progress" title={p?.title}
            >{#if p}<span class="sm-bar" aria-hidden="true"><i style:width="{p.percent}%"></i></span><span aria-hidden="true">{p.lines}<span class="sm-blocks">{' · '}{p.blocks}</span></span><span class="sr-only">{p.title}</span>{/if}</span
          >
          <span class="sm-cell sm-end">{#if r.on}<span class="sm-on">{r.on}</span>{/if}{#if r.current}<span class="sm-here">this pull request</span>{/if}</span>
        </a>
      {:else}
        <span role="menuitem" tabindex="-1" aria-disabled="true" class="sm-row gap" class:first={i === 0} class:last={i === menu.rows.length - 1} class:lane1={r.lane === 1}>
          <span class="sm-rail" aria-hidden="true"><span class="sm-node missing"></span></span>
          <span class="sr-only">{r.to > r.index ? `${r.index} to ${r.to}` : r.index} of {menu.count}:</span>
          <span class="sm-line"><span class="sm-gap">{r.text}</span></span>
        </span>
      {/if}
    {/each}
  </div>
  {#if status.summary}
    <p class="sm-status" role="status">
      {status.summary}
      {#if recheck}<button type="button" class="sm-again" onclick={onretry}>Check again</button>{/if}
    </p>
  {/if}
</div>

<style>
  .sm {
    /* The rail's lane, and where its line and nodes sit in it. */
    --lane: 34px;
    --node: 20px;
    /* The rail line's x in the lane; the node is centred on it. */
    --x: 16px;
    /* Line 1's height: the node and the rail's joins are centred on it. */
    --l1: 20px;
    --pad-y: 8px;
    width: max-content;
    min-width: min(420px, calc(100vw - 46px));
    max-width: min(640px, calc(100vw - 46px));
    font-size: 13px;
    line-height: var(--l1);
  }
  .sm.forked {
    --rail: calc(var(--lane) + 16px);
  }
  .sm-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 24px;
    padding: 6px 10px 10px;
    margin-bottom: 4px;
    border-bottom: 1px solid var(--line);
  }
  .sm-name {
    font-weight: 650;
    white-space: nowrap;
  }
  .sm-hint {
    display: inline-flex;
    gap: 6px;
    font-size: 12px;
    color: var(--muted);
    white-space: nowrap;
  }
  .sm-inferred {
    cursor: help;
  }
  .sm-inferred::before {
    content: '·';
    margin-right: 6px;
  }

  /* One grid for the whole list: every row is a subgrid of it, so line 2's cells share their columns down the list. */
  .sm-list {
    display: grid;
    grid-template-columns: var(--rail, var(--lane)) repeat(4, auto) minmax(0, 1fr);
  }
  .sm-row {
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: subgrid;
    grid-template-rows: var(--l1) 18px;
    row-gap: 2px;
    align-items: center;
    padding: var(--pad-y) 10px var(--pad-y) 0;
    border-radius: 6px;
    color: var(--fg);
    text-decoration: none;
  }
  .sm-row.gap {
    grid-template-rows: var(--l1);
    color: var(--muted);
    font-size: 12px;
  }
  a.sm-row:hover,
  .sm-row:focus {
    background: var(--soft);
    outline: none;
  }
  .sm-row:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: -2px;
  }
  .sm-row.current {
    background: var(--code);
    box-shadow: inset 3px 0 var(--accent);
  }
  .sm-row.current:hover,
  .sm-row.current:focus {
    background: var(--soft);
  }
  .sm-row.current .sm-title {
    font-weight: 700;
  }

  /* The rail: spans the row's full height (through its padding), so the rows' lines join into one. */
  .sm-rail {
    grid-column: 1;
    grid-row: 1 / -1;
    align-self: stretch;
    position: relative;
    margin: calc(var(--pad-y) * -1) 0;
  }
  .sm-rail::before {
    content: '';
    position: absolute;
    left: var(--x);
    top: 0;
    bottom: 0;
    width: 2px;
    margin-left: -1px;
    background: var(--line);
  }
  /* The line starts at the first node and stops at the last. */
  .sm-row.first .sm-rail::before {
    top: calc(var(--pad-y) + var(--l1) / 2);
  }
  .sm-row.last .sm-rail::before {
    bottom: auto;
    height: calc(var(--pad-y) + var(--l1) / 2);
  }
  .sm-row.first.last .sm-rail::before {
    content: none;
  }
  .sm-node {
    position: absolute;
    left: var(--x);
    top: calc(var(--pad-y) + var(--l1) / 2);
    width: var(--node);
    height: var(--node);
    margin: calc(var(--node) / -2) 0 0 calc(var(--node) / -2);
    display: grid;
    place-items: center;
    border-radius: 50%;
    border: 1px solid var(--muted);
    background: var(--surface);
    color: var(--fg);
    font: 600 10px/1 ui-monospace, SFMono-Regular, Consolas, monospace;
    font-variant-numeric: tabular-nums;
  }
  /* A branch off the stack's line: its node sits a lane over, joined to the line. */
  .sm-row.lane1 .sm-node {
    left: calc(var(--x) + 16px);
  }
  .sm-row.lane1 .sm-rail::after {
    content: '';
    position: absolute;
    left: var(--x);
    top: calc(var(--pad-y) + var(--l1) / 2 - 1px);
    width: 16px;
    height: 2px;
    background: var(--line);
  }
  .sm-node.approved {
    border-color: var(--good);
    background: var(--good);
    color: var(--surface);
  }
  .sm-node.changes {
    border-color: var(--bad);
    background: var(--bad);
    color: var(--surface);
  }
  .sm-node.merged {
    border-color: var(--merged);
    background: var(--merged);
    color: var(--surface);
  }
  .sm-node.yours {
    border-color: var(--accent);
    background: var(--accent);
    color: var(--surface);
  }
  .sm-node.closed {
    border-color: var(--line);
    color: var(--muted);
  }
  .sm-node.draft:not(.approved, .changes) {
    border-style: dashed;
  }
  .sm-node.missing {
    --node: 12px;
    border-style: dashed;
  }

  .sm-line {
    grid-column: 2 / -1;
    grid-row: 1;
    display: flex;
    align-items: baseline;
    gap: 8px;
    min-width: 0;
  }
  .sm-num {
    flex: none;
    font: 12px/var(--l1) ui-monospace, SFMono-Regular, Consolas, monospace;
    font-variant-numeric: tabular-nums;
    color: var(--muted);
  }
  .sm-title {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 600;
  }
  .sm-gap {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  /* Line 2: one cell per column, empty where a row has nothing to say; a cell's own padding is the gap after it. */
  .sm-cell {
    grid-row: 2;
    font-size: 12px;
    line-height: 18px;
    color: var(--muted);
    white-space: nowrap;
  }
  .sm-cell:not(:empty) {
    padding-right: 12px;
  }
  .sm-state {
    grid-column: 2;
  }
  .sm-state.merged {
    color: var(--merged);
  }
  .sm-state.closed {
    color: var(--bad);
  }
  .sm-state.open {
    color: var(--fg);
  }
  .sm-ci {
    grid-column: 3;
  }
  .sm-ci.passing {
    color: var(--good);
  }
  .sm-ci.pending {
    color: var(--warn);
  }
  .sm-ci.failing {
    color: var(--bad);
    font-weight: 600;
  }
  .sm-review {
    grid-column: 4;
  }
  .sm-review.approved {
    color: var(--good);
  }
  .sm-review.changes {
    color: var(--bad);
  }
  .sm-progress {
    grid-column: 5;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-variant-numeric: tabular-nums;
  }
  .sm-bar {
    flex: none;
    position: relative;
    width: 40px;
    height: 4px;
    border-radius: 2px;
    background: var(--line);
    overflow: hidden;
  }
  .sm-bar i {
    position: absolute;
    inset: 0 auto 0 0;
    background: var(--good);
  }
  .sm-end {
    grid-column: 6;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    min-width: 0;
  }
  .sm-cell.sm-end:not(:empty) {
    padding-right: 0;
  }
  .sm-on {
    font-style: italic;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sm-here {
    font-size: 11px;
    font-weight: 650;
    color: var(--accent);
  }

  .sm-status {
    margin: 4px 0 0;
    padding: 8px 10px 4px;
    border-top: 1px solid var(--line);
    font-size: 12px;
    line-height: 18px;
    color: var(--muted);
    /* Never wider than the list: its words wrap. */
    width: 0;
    min-width: 100%;
  }
  .sm-again {
    border: 0;
    background: none;
    padding: 0;
    margin-left: 4px;
    font: inherit;
    color: var(--accent);
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  /* A phone: the panel is a sheet as wide as the window allows; line 2 keeps its columns and drops what is said elsewhere. */
  @media (max-width: 480px) {
    .sm {
      width: calc(100vw - 46px);
      min-width: 0;
    }
    .sm-blocks,
    .sm-bar {
      display: none;
    }
    .sm-cell:not(:empty) {
      padding-right: 8px;
    }
    /* The bar, the tint and the weight mark the row; the tag is still read out. */
    .sm-here {
      position: absolute;
      width: 1px;
      height: 1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
    }
  }
</style>
